import dayjs from '@srm/ui/lib/dayjs';
import { capitalize } from '@/utils/capitalize';
import { IConversationListItem } from '@/models/chatai/IConversationListItem';

export interface IConversationGroup {
    /** Стабильный ключ группы. */
    key: string;
    /** Видимый заголовок. */
    label: string;
    items: IConversationListItem[];
}

interface IBucketInfo {
    key: string;
    label: string;
    /** Порядок сортировки между группами; больше = выше. */
    sortDate: number;
}

interface IBucket extends IBucketInfo {
    items: IConversationListItem[];
}

const DAY_MS = 86_400_000;
const MONTH_PREFIX = '4_month_';

// Календарных дней между now и date (по локальной полуночи, не по 24-часовым
// периодам) — паритет с date-fns differenceInCalendarDays.
const diffCalendarDays = (now: Date, date: Date): number =>
    dayjs(now).startOf('day').diff(dayjs(date).startOf('day'), 'day');

/**
 * Декларативная таблица фиксированных bucket'ов в порядке от свежих к старым.
 * Первый match выигрывает; если ни один не сработал — fallback на месячный bucket.
 * Преимущество над if-цепочкой: добавление/перестановка категорий — изменение одной
 * строки таблицы. Тесты проверяют порядок и границы.
 */
// Все matcher'ы используют переданный `now`: date-fns isToday/isYesterday смотрят на
// системные часы и игнорируют параметр, что ломает unit-тесты с фиксированной NOW и
// в проде ведёт к неверной группировке, если на разных табах часы разошлись.
// differenceInCalendarDays учитывает локальную TZ — те же дни календаря, что и для юзера.
const FIXED_BUCKETS: Array<(date: Date, now: Date) => IBucketInfo | null> = [
    (date, now) =>
        diffCalendarDays(now, date) === 0 ? { key: '0_today', label: 'Сегодня', sortDate: now.getTime() } : null,
    (date, now) =>
        diffCalendarDays(now, date) === 1
            ? { key: '1_yesterday', label: 'Вчера', sortDate: now.getTime() - DAY_MS }
            : null,
    (date, now) =>
        diffCalendarDays(now, date) <= 7
            ? { key: '2_prev7', label: 'Предыдущие 7 дней', sortDate: now.getTime() - 7 * DAY_MS }
            : null,
    (date, now) =>
        diffCalendarDays(now, date) <= 30
            ? { key: '3_prev30', label: 'Предыдущие 30 дней', sortDate: now.getTime() - 30 * DAY_MS }
            : null,
];

/**
 * Группирует треды как ChatGPT: «Сегодня / Вчера / Предыдущие 7 дней /
 * Предыдущие 30 дней / месяцы (по месяцам, имя на ru-локали)».
 *
 * Внутри группы сортирует по updatedAt DESC.
 * Между группами — фиксированный порядок для первых 4, далее месяцы DESC.
 */
export function groupConversationsByDate(items: IConversationListItem[], now: Date = new Date()): IConversationGroup[] {
    const buckets = new Map<string, IBucket>();

    for (const item of items) {
        const date = dayjs(item.updatedAt).toDate();
        if (Number.isNaN(date.getTime())) continue;

        const bucketInfo = pickBucket(date, now);
        let bucket = buckets.get(bucketInfo.key);
        if (!bucket) {
            bucket = { ...bucketInfo, items: [] };
            buckets.set(bucketInfo.key, bucket);
        }
        bucket.items.push(item);
    }

    return Array.from(buckets.values())
        .map((b) => {
            b.items.sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
            return b;
        })
        .sort((a, b) => {
            // Месяцы — по убыванию sortDate; всё остальное — по строковому ключу
            // ('0_today' < '1_yesterday' < ... < '4_month_*').
            if (a.key.startsWith(MONTH_PREFIX) && b.key.startsWith(MONTH_PREFIX)) {
                return b.sortDate - a.sortDate;
            }
            return a.key.localeCompare(b.key);
        })
        .map(({ key, label, items }) => ({ key, label, items }));
}

function pickBucket(date: Date, now: Date): IBucketInfo {
    for (const matcher of FIXED_BUCKETS) {
        const hit = matcher(date, now);
        if (hit) return hit;
    }
    return monthBucket(date, now);
}

function monthBucket(date: Date, now: Date): IBucketInfo {
    const sameYear = date.getFullYear() === now.getFullYear();
    // .locale('ru') явно — standalone-форма месяца («Март»), не зависим от глобальной локали.
    const label = capitalize(
        dayjs(date)
            .locale('ru')
            .format(sameYear ? 'MMMM' : 'MMMM YYYY'),
    );
    // sortDate — первое число месяца, чтобы стабильно убывать.
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
    return {
        key: `${MONTH_PREFIX}${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}`,
        label,
        sortDate: monthStart,
    };
}
